import React, { useState, useEffect, useContext } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { Typeahead } from 'react-bootstrap-typeahead';
import { withTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { LoaderContext } from '../../../App';
import GHGEmissionMasterDataService from '../../../core/service/GHGEmissionMasterDataService';

const SCOPE_OPTIONS = ['Scope 1', 'Scope 2', 'Scope 3'];

const FactorFormModal = ({ show, onHide, datasetName, version, factor, sources, selectedSource, onSuccess, t, companyId = null }) => {
  const { setLoaderDisplay } = useContext(LoaderContext);
  const [formData, setFormData] = useState({
    emissionSourceId: '',
    ghg: '',
    efValue: '',
    unit: '',
    scope: '',
    gwpBasis: '',
    notes: ''
  });
  const [errors, setErrors] = useState({});
  const isEdit = !!factor;

  useEffect(() => {
    if (factor) {
      setFormData({
        emissionSourceId: factor.emissionSourceId || '',
        ghg: factor.ghg || '',
        efValue: factor.efValue || '',
        unit: factor.unit || '',
        scope: factor.scope || '',
        gwpBasis: factor.gwpBasis || '',
        notes: factor.notes || ''
      });
    } else if (selectedSource) {
      // Pre-fill with selected source when coming from "view factors"
      setFormData({
        emissionSourceId: selectedSource.id || '',
        ghg: '',
        efValue: '',
        unit: '',
        scope: '',
        gwpBasis: '',
        notes: ''
      });
    } else {
      setFormData({
        emissionSourceId: '',
        ghg: '',
        efValue: '',
        unit: '',
        scope: '',
        gwpBasis: '',
        notes: ''
      });
    }
    setErrors({});
  }, [factor, selectedSource, show]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validate = () => {
    const newErrors = {};
    
    // Mandatory fields validation
    if (!formData.emissionSourceId) {
      newErrors.emissionSourceId = t("FactorFormModal.errors.emissionSourceIdRequired.label");
    }
    if (!formData.ghg || formData.ghg.trim() === '') {
      newErrors.ghg = t("FactorFormModal.errors.ghgRequired.label");
    }
    if (!formData.efValue || formData.efValue === '') {
      newErrors.efValue = t("FactorFormModal.errors.efValueRequired.label");
    } else if (isNaN(parseFloat(formData.efValue))) {
      newErrors.efValue = t("FactorFormModal.errors.efValueInvalid.label");
    }
    if (!formData.unit || formData.unit.trim() === '') {
      newErrors.unit = t("FactorFormModal.errors.unitRequired.label");
    }
    
    return newErrors;
  };

  const handleSubmit = () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoaderDisplay(true);
    const payload = {
      ...formData,
      efValue: formData.efValue ? parseFloat(formData.efValue) : null,
      emissionSourceId: parseInt(formData.emissionSourceId)
    };

    const promise = isEdit
      ? GHGEmissionMasterDataService.updateFactor(datasetName, version, factor.id, payload, companyId)
      : GHGEmissionMasterDataService.createFactor(datasetName, version, payload, companyId);

    promise
      .then((response) => {
        if (response.status === 200 || response.status === 201) {
          toast.success(
            isEdit
              ? t("FactorFormModal.updateSuccess.label")
              : t("FactorFormModal.createSuccess.label"),
            {
              position: toast.POSITION.TOP_RIGHT,
            }
          );
          onSuccess();
        }
      })
      .catch((error) => {
      })
      .finally(() => {
        setLoaderDisplay(false);
      });
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered data-testid="factor-form-modal">
      <Modal.Header closeButton>
        <Modal.Title>
          {isEdit ? t("FactorFormModal.editTitle.label") : t("FactorFormModal.addTitle.label")}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>
              {t("FactorFormModal.emissionSource.label")} <span className="text-danger">*</span>
            </Form.Label>
            <Typeahead
              id="emission-source-select"
              labelKey="name"
              options={sources}
              placeholder={t("FactorFormModal.selectSource.label")}
              selected={sources.filter(s => s.id === parseInt(formData.emissionSourceId))}
              onChange={(selected) => {
                handleChange('emissionSourceId', selected[0] ? selected[0].id : '');
              }}
              clearButton
              className={errors.emissionSourceId ? 'is-invalid' : ''}
              inputProps={{
                'data-testid': 'emission-source-select'
              }}
            />
            {errors.emissionSourceId && (
              <div className="invalid-feedback d-block">{errors.emissionSourceId}</div>
            )}
          </Form.Group>

          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>
                  {t("FactorFormModal.ghg.label")} <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={formData.ghg}
                  onChange={(e) => handleChange('ghg', e.target.value)}
                  placeholder="CO2, CH4, N2O, etc."
                  isInvalid={!!errors.ghg}
                  data-testid="ghg-input"
                />
                <Form.Control.Feedback type="invalid">{errors.ghg}</Form.Control.Feedback>
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>
                  {t("FactorFormModal.efValue.label")} <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  step="any"
                  value={formData.efValue}
                  onChange={(e) => handleChange('efValue', e.target.value)}
                  isInvalid={!!errors.efValue}
                  data-testid="ef-value-input"
                />
                <Form.Control.Feedback type="invalid">{errors.efValue}</Form.Control.Feedback>
              </Form.Group>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>
                  {t("FactorFormModal.unit.label")} <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={formData.unit}
                  onChange={(e) => handleChange('unit', e.target.value)}
                  placeholder="kg CO2e"
                  isInvalid={!!errors.unit}
                  data-testid="unit-input"
                />
                <Form.Control.Feedback type="invalid">{errors.unit}</Form.Control.Feedback>
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>{t("FactorFormModal.scope.label")}</Form.Label>
                <Form.Select
                  value={formData.scope}
                  onChange={(e) => handleChange('scope', e.target.value)}
                  data-testid="scope-select"
                >
                  <option value="">{t("FactorFormModal.selectScope.label")}</option>
                  {SCOPE_OPTIONS.map((scope) => (
                    <option key={scope} value={scope}>
                      {scope}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>{t("FactorFormModal.gwpBasis.label")}</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.gwpBasis}
                  onChange={(e) => handleChange('gwpBasis', e.target.value)}
                  placeholder="AR5, AR6, etc."
                  data-testid="gwp-basis-input"
                />
              </Form.Group>
            </div>
          </div>

          <Form.Group className="mb-3">
            <Form.Label>{t("FactorFormModal.notes.label")}</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              data-testid="notes-input"
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          {t("Cancel.label")}
        </Button>
        <Button variant="primary" onClick={handleSubmit} data-testid="submit-button">
          {isEdit ? t("Update.label") : t("Create.label")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default withTranslation()(FactorFormModal);
